import { api } from "./client";
import type { OrgSummary } from "./types";
import type { Intercompany } from "./orgs7";

/*
 * Organizations (server/orgs.js): the header's switcher and Settings > Organizations. Each organization has its own
 * books, settings, GSTIN, branches, currency and number series; a person has a role in each one they belong to.
 */

export type Copyable = "accounts" | "taxes" | "templates";
export type MyOrg = OrgSummary & { role: string | null; is_admin: boolean; base_currency?: string; created_at: string; copied_from: number | null; copied: Copyable[] };
/** An archived organization, listed to its Admins only (to restore or delete). */
export type ArchivedOrg = { id: number; name: string; gstin: string | null; created_at: string; archived_at: string; archived_by: string | null };
export type OrgList = { current: number; can_create: boolean; organizations: MyOrg[]; archived?: ArchivedOrg[] };
export type OrgMember = { id: string; full_name: string; email: string; role_id: string; role: string | null; active: boolean };
export type OrgMembers = { members: OrgMember[]; roles: { id: string; name: string }[] };
export type OrgIn = { name: string; gstin?: string | null; copy_from?: number | null; copy?: Copyable[] };

/** Where an organization's outside integrations reach it (organization n: /api/v1/o/<n>/...). */
export type Integration = { key: string; label: string; method: string; url: string; also?: string[]; header?: string; /** For a callback: how this organization checks it (server/callback-auth.js). */ scheme?: string; note: string };
export type Integrations = { organization: OrgSummary; items: Integration[]; note: string };

/** Consolidated reports across your organizations (server/orgs-consolidate.js): every row has a column per organization and a total. */
export type ConsReport = "pl" | "balance-sheet" | "receivables" | "payables";
/** With automatic eliminations (wave 7), every row and total also has `eliminations` and `consolidated` (total + eliminations). */
export type ConsCols = { by_org: Record<string, string>; total: string; eliminations?: string; consolidated?: string };
export type ConsRow = ConsCols & { key: string; code: string; name: string };
export type ConsSection = ConsCols & { key: string; label: string; accounts: ConsRow[] };
export type ConsFigure = ConsCols & { label: string };
export type ConsParty = { name: string; by_org: Record<string, string>; total: string; unapplied_credit: string; net: string; eliminations?: string; consolidated?: string } & Record<string, unknown>;
export type Consolidated = {
  report: ConsReport;
  label: string;
  currency: string;
  from?: string;
  to?: string;
  as_of?: string;
  organizations: { id: number; name: string; base_currency: string; rate: string }[];
  skipped: { id: number; name: string; status: number; reason: string }[];
  eliminations: string;
  sections?: ConsSection[];
  gross_profit?: ConsFigure;
  operating_profit?: ConsFigure;
  net_profit?: ConsFigure;
  assets?: ConsSection[];
  liabilities?: ConsSection[];
  equity?: ConsSection[];
  total_assets?: ConsFigure;
  total_liabilities?: ConsFigure;
  total_equity?: ConsFigure;
  total_liabilities_and_equity?: ConsFigure;
  difference?: ConsFigure;
  buckets?: { key: string; label: string }[];
  parties?: ConsParty[];
  totals?: Record<string, string>;
  net_by_org?: ConsFigure;
  net_consolidated?: { label: string; eliminations: string; consolidated: string };
  intercompany?: Intercompany;
};

const O = "/admin/organizations";

export const orgs = {
  list: () => api<OrgList>("GET", O),
  create: (body: OrgIn) => api<MyOrg>("POST", O, body),
  rename: (id: number, name: string) => api<OrgSummary>("PATCH", `${O}/${id}`, { name }),
  /** Remembers the choice on the server; the page then reloads into that organization. */
  switchTo: (id: number) => api<{ organization: OrgSummary }>("POST", `${O}/switch`, { org_id: id }),
  members: (id: number) => api<OrgMembers>("GET", `${O}/${id}/members`),
  addMember: (id: number, email: string, role_id: string) => api<OrgMembers>("POST", `${O}/${id}/members`, { email, role_id }),
  archive: (id: number) => api<{ id: number; name: string; archived_at: string }>("POST", `${O}/${id}/archive`, {}),
  restore: (id: number) => api<OrgSummary>("POST", `${O}/${id}/restore`, {}),
  /** For good, after a backup the server makes first; `confirm` is the organization's name, typed. */
  remove: (id: number, confirm: string) => api<{ deleted: number; name: string; backup: string }>("DELETE", `${O}/${id}`, { confirm }),
  integrations: (id: number) => api<Integrations>("GET", `${O}/${id}/integrations`),
  consolidated: (q: Record<string, string>) => api<Consolidated>("GET", `${O}/consolidated?${new URLSearchParams(Object.entries(q).filter(([, v]) => v !== ""))}`),
};
